import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { generateCorrectAnswerPatterns } from '../../../utils/quizUtils';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('SentenceRearrangement');

// 配列シャッフル関数
function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// SentenceRearrangementコンポーネント
const SentenceRearrangement = ({ question, answered, isTimeUp, handleSubmit, quickAnswerMode = 'standard' }) => {
  const [available, setAvailable] = useState([]);
  const [sentence, setSentence] = useState([]);

  // フォームの無効化状態を判定
  const isDisabled = answered || isTimeUp;
  
  // 初期化
  useEffect(() => {
    const initialWords = question.words.map((word, index) => ({ id: `word-${index}`, content: word }));
    setAvailable(shuffleArray(initialWords));
    setSentence([]);
  }, [question.words]);

  const displayWord = (content) => {
    // 句読点の場合はそのまま返す
    if (/^[,.:;!?]$/.test(content)) {
      return content;
    }
    
    // コロンで分割（タグ情報がある場合）
    const baseWord = content.split(':')[0];
    
    // 「I」の場合は大文字のまま表示
    if (baseWord === 'I') {
      return baseWord;
    }
    
    // その他の単語は1文字目を小文字にして表示
    if (baseWord.length > 0) {
      return baseWord.charAt(0).toLowerCase() + baseWord.slice(1);
    }
    
    return baseWord;
  };

  // ドラッグ終了時の処理を修正
  const onDragEnd = (result) => {
    if (isDisabled) return;
    
    const { source, destination } = result;
    
    // ドロップ先がない場合は何もしない
    if (!destination) {
      return;
    }

    // 同じ場所にドロップした場合は何もしない
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    try {
      // availableからsentenceへの移動
      if (source.droppableId === 'available' && destination.droppableId === 'sentence') {
        const newAvailable = [...available];
        const [movedItem] = newAvailable.splice(source.index, 1);
        const newSentence = [...sentence];
        newSentence.splice(destination.index, 0, movedItem);
        setAvailable(newAvailable);
        setSentence(newSentence);
      }
      // sentenceからavailableへの移動
      else if (source.droppableId === 'sentence' && destination.droppableId === 'available') {
        const newSentence = [...sentence];
        const [movedItem] = newSentence.splice(source.index, 1);
        const newAvailable = [...available];
        newAvailable.splice(destination.index, 0, movedItem);
        setSentence(newSentence);
        setAvailable(newAvailable);
      }
      // sentence内での移動
      else if (source.droppableId === 'sentence' && destination.droppableId === 'sentence') {
        const newSentence = [...sentence];
        const [movedItem] = newSentence.splice(source.index, 1);
        newSentence.splice(destination.index, 0, movedItem);
        setSentence(newSentence);
      }
    } catch (error) {
      logger.error('Drag and drop error:', error);
    }
  };

  // ダブルクリック処理を修正
  const handleDoubleClick = (word, from) => {
    if (isDisabled) return;

    try {
      if (from === 'available') {
        setAvailable(prev => prev.filter(w => w.id !== word.id));
        setSentence(prev => [...prev, word]);
      } else {
        setSentence(prev => prev.filter(w => w.id !== word.id));
        setAvailable(prev => [...prev, word]);
      }
    } catch (error) {
      logger.error('Double click error:', error);
    }
  };

  const onSubmit = () => {
    if (isDisabled) return;

    try {
      // 現在の文章を構築
      const sentenceText = sentence.map(word => word.content).join(' ') + (question.lastPunctuation || '');
      
      // 同じ単語（大文字・小文字違い）の重複に対応した正解パターンを生成
      const correctPatterns = generateCorrectAnswerPatterns(question.sentence, [...available, ...sentence]);
      
      // デバッグ用ログ
      logger.debug('英文並び替え正解判定開始', {
        correctSentence: question.sentence,
        userAnswer: sentenceText,
        patterns: correctPatterns
      });
      
      // 正解判定の改善：
      // 1. 余分なスペースを除去
      // 2. 文の先頭は大文字、それ以外は元の大文字小文字を維持
      const normalizeForComparison = (text) => {
        const cleaned = text.trim().replace(/\s+/g, ' ');
        // 文の先頭のみ大文字に統一、それ以外はそのまま
        if (cleaned.length > 0) {
          return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
        }
        return cleaned;
      };
      
      const userNormalized = normalizeForComparison(sentenceText);
      
      // いずれかの正解パターンと一致するかチェック
      const isCorrect = correctPatterns.some(pattern => {
        const patternNormalized = normalizeForComparison(pattern);
        logger.debug('パターン比較', {
          userNormalized,
          patternNormalized,
          isMatch: userNormalized === patternNormalized
        });
        return userNormalized === patternNormalized;
      });
      
      logger.info('英文並び替え判定結果', {
        isCorrect,
        userAnswer: sentenceText,
        correctAnswer: question.sentence
      });
      
      const userAnswer = sentenceText;
      
      handleSubmit(isCorrect, isCorrect ? '正解です！' : `不正解です。正解は「${question.sentence}」です。`, question.sentence, userAnswer);
      
    } catch (error) {
      logger.error('回答提出エラー:', error);
      alert('回答の提出中にエラーが発生しました。もう一度お試しください。');
    }
  };

  return (
    <div className={isDisabled ? 'opacity-70' : ''}>
      <p className="text-sm font-medium text-purple-900 mb-2">
        日本文の意味になるように、下の英単語を並べ替えなさい。
      </p>
      <p className="text-md text-gray-700 mb-4">{question.japaneseTranslation}</p>
      <p className="text-xs text-right text-gray-500 mb-4">
        ※ドラッグ&ドロップまたはダブルクリックで単語を移動できます
      </p>

      <DragDropContext onDragEnd={onDragEnd}>
        {/* 文章作成エリア */}
        <div className="mb-4 border p-4 rounded min-h-[100px]">
          <h4 className="text-gray-500 mb-2 text-sm text-center">ここに並べてください</h4>
          <Droppable droppableId="sentence" direction="horizontal">
            {(provided) => (
              <div 
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-wrap gap-2"
              >
                {sentence.map((word, index) => (
                  <Draggable
                    key={word.id}
                    draggableId={word.id}
                    index={index}
                    isDragDisabled={isDisabled}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onDoubleClick={() => handleDoubleClick(word, 'sentence')}
                        className={`px-3 py-1 rounded cursor-pointer 
                          ${/^[,.:;!?]$/.test(word.content) ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700 hover:bg-green-200'}
                          ${isDisabled ? 'cursor-not-allowed' : ''}`}
                      >
                        {displayWord(word.content)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {question.lastPunctuation && (
                  <div className="px-3 py-1 bg-gray-50 text-gray-500 rounded select-none">
                    {question.lastPunctuation}
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </div>

        {/* 利用可能な単語エリア */}
        <div className="border p-4 rounded mb-4">
          <h4 className="font-semibold text-purple-700 mb-2">利用可能な単語</h4>
          <Droppable droppableId="available" direction="horizontal" isDropDisabled={true}>
            {(provided) => (
              <div 
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-wrap gap-2"
              >
                {available.map((word, index) => (
                  <Draggable
                    key={word.id}
                    draggableId={word.id}
                    index={index}
                    isDragDisabled={isDisabled}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onDoubleClick={() => handleDoubleClick(word, 'available')}
                        className={`px-3 py-1 rounded cursor-pointer
                          ${/^[,.:;!?]$/.test(word.content) ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}
                          ${isDisabled ? 'cursor-not-allowed' : ''}`}
                      >
                        {displayWord(word.content)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>

      <div className="flex justify-end mt-2">
        <button
          onClick={onSubmit}
          className={`px-6 py-2 rounded-lg 
            ${isDisabled ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
          disabled={isDisabled || sentence.length === 0}
          aria-label="回答を送信するボタン"
        >
          回答する
        </button>
      </div>
    </div>
  );
};

SentenceRearrangement.propTypes = {
  question: PropTypes.object.isRequired,
  answered: PropTypes.bool.isRequired,
  isTimeUp: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  quickAnswerMode: PropTypes.string,  // クイック回答モード設定
};

export default SentenceRearrangement; 